import React, { useState, useCallback } from 'react';
import { Button, Input, FormGroup, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import { X } from 'lucide-react';
import axios from 'axios';
import { APP_TOKEN } from '../../../../../../../constants';

const GstVerificationInput = ({ vendorId, refetch }) => {
  const [gstNumber, setGstNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifiedData, setVerifiedData] = useState(null);
  const [saving, setSaving] = useState(false);

  const API_BASE = process.env.REACT_APP_REST_API_ENDPOINT;
  const SYSTEM_TOKEN = process.env.REACT_APP_API_TOKEN;

  const extractErrorMessage = (error, fallback = '') => {
    const data = error?.response?.data;

    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        return (
          parsed?.message ||
          parsed?.error ||
          parsed?.data?.message ||
          parsed?.data?.error ||
          data
        );
      } catch {
        return data || fallback;
      }
    }

    return (
      data?.message ||
      data?.error ||
      data?.data?.message ||
      data?.data?.error ||
      error?.message ||
      fallback
    );
  };

  const isPermissionError = (error) => {
    const status = error?.response?.status;
    const msg = extractErrorMessage(error, '');
    const normalized = String(msg).toLowerCase();
    return (
      status === 401 ||
      status === 403 ||
      normalized.includes('permission') ||
      normalized.includes('unauthorized')
    );
  };

  const getAuthToken = () => {
    const auth = JSON.parse(localStorage.getItem(APP_TOKEN));
    return auth?.token || null;
  };

  const buildHeaders = (token) => {
    if (!token) return null;
    return {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    };
  };

  const handleVerifyGst = useCallback(async () => {
    if (gstNumber.length !== 15) {
      toast.error('Enter valid 15 digit GST number');
      return;
    }

    const userToken = getAuthToken();
    const primaryToken = userToken || SYSTEM_TOKEN;
    if (!primaryToken) {
      toast.error('Auth token missing');
      return;
    }

    setLoading(true);
    setVerifiedData(null);

    try {
      const makeRequest = async (token) =>
        axios.get(`${API_BASE}trader/gst-information`, {
          params: { gstNumber },
          headers: buildHeaders(token),
          validateStatus: () => true,
        });

      let response = await makeRequest(primaryToken);

      if (
        userToken &&
        SYSTEM_TOKEN &&
        userToken !== SYSTEM_TOKEN &&
        response?.status >= 400 &&
        isPermissionError({ response })
      ) {
        response = await makeRequest(SYSTEM_TOKEN);
      }

      if (response?.status >= 400) {
        const requestError = new Error(
          extractErrorMessage({ response }, 'GST verification failed'),
        );
        requestError.response = response;
        throw requestError;
      }

      const source = response?.data?.data?.result?.source_output;
      if (!source) {
        throw new Error('Invalid GST data');
      }

      const addressFields =
        source?.principal_place_of_business_fields
          ?.principal_place_of_business_address;

      const doorNumber = addressFields?.door_number || '';
      const street = addressFields?.street || '';
      const location = addressFields?.location || '';
      const city =
        addressFields?.dst || addressFields?.city || addressFields?.district || '';
      const stateName = addressFields?.state_name || '';
      const pincode = addressFields?.pincode || '';

      // Construct full address
      const addressParts = [
        doorNumber,
        street,
        location,
        city,
        stateName,
        pincode,
      ].filter(Boolean);
      const fullAddress = addressParts.join(', ');

      setVerifiedData({
        legalName: source.legal_name || '',
        gstin: source.gstin || '',
        doorNumber,
        street,
        location,
        city,
        stateName,
        pincode,
        fullAddress,
        gstinStatus: source.gstin_status || 'Active',
      });

      toast.success('GST verified successfully');
    } catch (error) {
      console.error('GST Verify Error:', error);

      const msg = extractErrorMessage(error, 'GST verification failed');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [gstNumber, API_BASE, SYSTEM_TOKEN]);

  const handleSaveGst = useCallback(async () => {
    if (!verifiedData) return;

    const userToken = getAuthToken();
    const primaryToken = userToken || SYSTEM_TOKEN;
    if (!primaryToken) {
      toast.error('Auth token missing');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        entityName: verifiedData.legalName,
        tradeName: verifiedData.legalName,
        tradeGstIn: verifiedData.gstin,
        legalName: verifiedData.legalName,
        gstInStatus: verifiedData.gstinStatus,
        isGstVerified: true,
        gstLocation: verifiedData.fullAddress,
        gstCity: verifiedData.city,
        gstState: verifiedData.stateName,
      };

      const makeRequest = async (token) =>
        axios.put(`${API_BASE}trader/gst-info/${vendorId}`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          validateStatus: () => true,
        });

      let response = await makeRequest(primaryToken);

      if (
        userToken &&
        SYSTEM_TOKEN &&
        userToken !== SYSTEM_TOKEN &&
        response?.status >= 400 &&
        isPermissionError({ response })
      ) {
        response = await makeRequest(SYSTEM_TOKEN);
      }

      if (response?.status >= 400) {
        const requestError = new Error(
          extractErrorMessage({ response }, 'Failed to save GST'),
        );
        requestError.response = response;
        throw requestError;
      }

      toast.success('GST saved successfully');
      setGstNumber('');
      setVerifiedData(null);
      if (refetch) {
        refetch();
      }
    } catch (error) {
      console.error('GST Save Error:', error);

      const msg = extractErrorMessage(error, 'Failed to save GST');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }, [verifiedData, API_BASE, refetch, vendorId, SYSTEM_TOKEN]);

  const handleReset = () => {
    setGstNumber('');
    setVerifiedData(null);
  };

  return (
    <div className="mt-3">
      {!verifiedData ? (
        <FormGroup className="d-flex gap-2">
          <Input
            value={gstNumber}
            maxLength={15}
            placeholder="Enter GST Number"
            onChange={(e) => setGstNumber(e.target.value.toUpperCase())}
            disabled={loading}
          />
          <Button
            color="primary"
            onClick={handleVerifyGst}
            disabled={loading || gstNumber.length !== 15}
            type="button"
          >
            {loading ? <Spinner size="sm" /> : 'Verify'}
          </Button>
        </FormGroup>
      ) : (
        <div className="border rounded p-3 position-relative">
          <button
            type="button"
            onClick={handleReset}
            className="btn btn-link position-absolute top-0 end-0 p-2 text-danger"
            style={{ border: 'none', background: 'none' }}
            disabled={saving}
          >
            <X size={20} />
          </button>

          <div className="mb-3">
            <h6 className="fw-bold mb-2">{verifiedData.legalName}</h6>
            <p className="text-muted mb-1 small">
              {verifiedData.doorNumber && `${verifiedData.doorNumber}, `}
              {verifiedData.street && `${verifiedData.street}, `}
              {verifiedData.location}
            </p>
            <p className="text-muted mb-0 small">
              {verifiedData.city && `${verifiedData.city}, `}
              {verifiedData.stateName}
              {verifiedData.pincode && ` - ${verifiedData.pincode}`}
            </p>
          </div>

          <Button
            color="success"
            onClick={handleSaveGst}
            disabled={saving}
            size="sm"
            type="button"
          >
            {saving ? <Spinner size="sm" /> : 'Save'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default GstVerificationInput;
